import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { imgUrl } from '../utils/imgUrl';
import { useSyncedTick } from '../utils/syncedSlideshow';

const CategoryCard = ({ category }) => {
  // Build slides: videos first, then images
  const slides = [];
  if (category.videos && category.videos.length > 0) {
    category.videos.forEach((v) => slides.push({ type: 'video', src: v }));
  }
  if (category.images && category.images.length > 0) {
    category.images.forEach((img) => slides.push({ type: 'image', src: img }));
  } else if (category.image) {
    slides.push({ type: 'image', src: category.image });
  }
  const hasMultipleSlides = slides.length > 1;

  // Shared synchronized tick so every card on the page advances together.
  const tick = useSyncedTick();
  const currentIndex = hasMultipleSlides ? tick % slides.length : 0;

  return (
    <Link
      to={`/products?category=${category.id}`}
      className="group relative h-64 sm:h-80 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:scale-105"
    >
      {/* Background slides */}
      <div className="absolute inset-0 bg-gray-900">
        {slides.map((slide, index) =>
          slide.type === 'video' ? (
            <video
              key={index}
              src={imgUrl(slide.src)}
              poster={imgUrl(category.image, 800)}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                index === currentIndex ? 'opacity-100' : 'opacity-0'
              }`}
              style={{ transitionTimingFunction: 'ease-in-out' }}
              data-testid={`category-video-${category.id}-${index}`}
            />
          ) : (
            <img
              key={index}
              src={imgUrl(slide.src, 800)}
              alt={category.name}
              loading="lazy"
              decoding="async"
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                index === currentIndex ? 'opacity-100' : 'opacity-0'
              }`}
              style={{ transitionTimingFunction: 'ease-in-out' }}
            />
          )
        )}
      </div>
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/50 to-transparent pointer-events-none" />
      
      {/* Content */}
      <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 text-white">
        <h3 className="text-xl sm:text-2xl font-bold mb-1 sm:mb-2">{category.name}</h3>
        <p className="text-gray-200 text-xs sm:text-sm mb-3 sm:mb-4">{category.description}</p>
        <div className="flex items-center text-cyan-400 font-medium text-sm sm:text-base">
          <span>Shop Now</span>
          <ArrowRight size={16} className="ml-2 group-hover:translate-x-2 transition-transform sm:w-[18px] sm:h-[18px]" />
        </div>
      </div>

      {/* Slide Indicators */}
      {hasMultipleSlides && (
        <div className="absolute top-4 right-4 flex space-x-1">
          {slides.map((_, index) => (
            <div
              key={index}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentIndex ? 'w-6 bg-cyan-400' : 'w-2 bg-white/50'
              }`}
            />
          ))}
        </div>
      )}
    </Link>
  );
};

export default CategoryCard;
